import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eraser, PenLine, Loader2 } from "lucide-react";

export default function SignaturePad({ open, onOpenChange, roleLabel, defaultNom, onSign, busy }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const [nom, setNom] = useState(defaultNom || "");
  const [lieu, setLieu] = useState("");

  useEffect(() => { if (open) { setNom(defaultNom || ""); setEmpty(true); setTimeout(setup, 50); } }, [open, defaultNom]);

  const setup = () => {
    const c = canvasRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio; c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#172132";
  };

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setEmpty(false); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); };

  const save = () => onSign({ nom, lieu, image: canvasRef.current.toDataURL("image/png") });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="signature-dialog">
        <DialogHeader><DialogTitle className="text-dd-blue flex items-center gap-2"><PenLine size={18} /> Signature — {roleLabel}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-dd-night">Nom et prénom</Label>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} className="h-11 mt-1" data-testid="signature-nom" /></div>
          <div><Label className="text-dd-night">Lieu</Label>
            <Input value={lieu} onChange={(e) => setLieu(e.target.value)} className="h-11 mt-1" placeholder="Neuchâtel" data-testid="signature-lieu" /></div>
        </div>
        <div className="relative">
          <canvas ref={canvasRef} data-testid="signature-canvas"
            className="w-full h-56 bg-white border-2 border-dashed border-dd-warm rounded-md touch-none cursor-crosshair"
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />
          {empty && <div className="absolute inset-0 flex items-center justify-center text-dd-warm text-sm pointer-events-none">Signez ici avec le doigt ou le stylet</div>}
          <div className="absolute bottom-6 left-4 right-4 border-b border-dd-light pointer-events-none" />
        </div>
        <p className="text-xs text-dd-warm">Signature électronique simple (non qualifiée, SCSE). En signant, la partie confirme l'exactitude du procès-verbal.</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={clear} className="h-11 border-dd-light" data-testid="signature-clear"><Eraser size={16} className="mr-1" /> Effacer</Button>
          <Button onClick={save} disabled={empty || !nom.trim() || busy} className="h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="signature-save">
            {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <PenLine size={16} className="mr-1" />} Valider la signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
